
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslatedContent } from "@/hooks/useTranslatedContent";
import { useReveal } from "@/hooks/useReveal";
import { getPhotos, getPhotoUrl } from "@/services/photoService";
import { getLocalizedPath } from "@/components/LocalizedRoute";
import { useEffect, useState, useRef, useCallback } from "react";
import { ArrowRight } from "lucide-react";
import type { Photo } from "@/types/supabase";

const AboutSection = () => {
  const { language } = useLanguage();
  const [featuredPhotos, setFeaturedPhotos] = useState<Photo[]>([]);

  const textReveal = useReveal<HTMLDivElement>();
  const statsReveal = useReveal<HTMLDivElement>();

  // Parallax refs for photos
  const photoColRef = useRef<HTMLDivElement>(null);
  const img1Ref = useRef<HTMLImageElement>(null);
  const img2Ref = useRef<HTMLImageElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    getPhotos()
      .then((all) => {
        const featured = all.filter((p) => p.featured);
        const pool = featured.length >= 2 ? featured : [...featured, ...all.filter((p) => !p.featured)];
        setFeaturedPhotos(pool.slice(0, 2));
      })
      .catch(() => {});
  }, []);

  const updateParallax = useCallback(() => {
    const col = photoColRef.current;
    if (!col) return;
    const rect = col.getBoundingClientRect();
    const viewH = window.innerHeight;
    const centre = rect.top + rect.height / 2 - viewH / 2;

    if (img1Ref.current) {
      const t = centre * 0.12;
      img1Ref.current.style.transform = `translateY(${t}px) scale(1.08)`;
    }
    if (img2Ref.current) {
      const t = centre * -0.08;
      img2Ref.current.style.transform = `translateY(${t}px) scale(1.06)`;
    }
  }, []);

  useEffect(() => {
    if (featuredPhotos.length === 0) return;
    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateParallax);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    updateParallax();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [featuredPhotos.length, updateParallax]);

  const aboutContent = {
    heading: "Votre Oasis de Détente au Grau du Roi",
    paragraph1:
      "Nichée dans un quartier paisible du Grau du Roi, la Villa Douce Vague vous offre un cadre idyllique pour vos vacances en bord de mer. À seulement quelques minutes à pied des plages de sable fin et du célèbre port de pêche, notre villa allie confort moderne et charme méditerranéen.",
    paragraph2:
      "Avec ses 140m² d'espace de vie, sa piscine privée chauffée et son jardin luxuriant, la villa Douce Vague peut accueillir jusqu'à 8 personnes dans un cadre élégant et raffiné.",
    buttonText: "Découvrir les équipements",
  };

  const { translatedContent, isLoading } = useTranslatedContent(aboutContent);

  const stats = [
    { value: "140", unit: "m²", label: "Surface" },
    { value: "8", unit: "pers.", label: "Capacité" },
    { value: "4", unit: "", label: "Chambres" },
    { value: "5 min", unit: "", label: "des plages" },
  ];

  return (
    <section id="about" className="bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-32">

        {/* Stats strip */}
        <div
          ref={statsReveal.ref}
          className={`grid grid-cols-2 sm:grid-cols-4 gap-6 mb-20 md:mb-28 reveal ${statsReveal.revealed ? "in" : ""}`}
        >
          {stats.map((s, i) => (
            <div key={i} className="text-center group">
              <div className="font-playfair text-4xl md:text-5xl font-bold text-villa-text tracking-tight leading-none transition-transform duration-500 group-hover:scale-105">
                {s.value}
                <span className="text-villa-turquoise text-2xl md:text-3xl">{s.unit}</span>
              </div>
              <p className="mt-2 text-xs text-villa-text-secondary font-medium tracking-[0.12em] uppercase">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Text */}
          <div ref={textReveal.ref} className={`reveal ${textReveal.revealed ? "in" : ""}`}>
            <p className="eyebrow mb-4">La Villa</p>
            <h2 className="heading-lg text-villa-text mb-6 max-w-md">
              {isLoading ? aboutContent.heading : translatedContent.heading}
            </h2>
            <div className="w-10 h-px bg-villa-turquoise mb-8" />
            <p className="body-lg mb-5">
              {isLoading ? aboutContent.paragraph1 : translatedContent.paragraph1}
            </p>
            <p className="body-lg mb-10">
              {isLoading ? aboutContent.paragraph2 : translatedContent.paragraph2}
            </p>
            <Link
              to={getLocalizedPath("/equipements", language)}
              className="inline-flex items-center gap-2 text-villa-turquoise font-medium text-[15px] group"
            >
              {isLoading ? aboutContent.buttonText : translatedContent.buttonText}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Photos — parallax on scroll */}
          <div
            ref={photoColRef}
            className="grid grid-cols-2 gap-4"
            style={{ minHeight: 440 }}
          >
            {featuredPhotos.length > 0 ? (
              <>
                {/* Large photo */}
                <div
                  className="col-span-1 rounded-3xl overflow-hidden"
                  style={{ gridRow: "span 2", height: 440 }}
                >
                  <img
                    ref={img1Ref}
                    src={`${getPhotoUrl(featuredPhotos[0].storage_path)}?width=800&quality=85`}
                    alt={featuredPhotos[0].title || "Villa Douce Vague"}
                    className="w-full h-full object-cover"
                    style={{ willChange: "transform", transformOrigin: "center" }}
                    loading="lazy"
                  />
                </div>

                {/* Second photo */}
                {featuredPhotos[1] ? (
                  <div className="rounded-3xl overflow-hidden" style={{ height: 210 }}>
                    <img
                      ref={img2Ref}
                      src={`${getPhotoUrl(featuredPhotos[1].storage_path)}?width=600&quality=85`}
                      alt={featuredPhotos[1].title || "Villa Douce Vague"}
                      className="w-full h-full object-cover"
                      style={{ willChange: "transform", transformOrigin: "center" }}
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="rounded-3xl bg-villa-offwhite" style={{ height: 210 }} />
                )}

                {/* Accent card */}
                <div className="rounded-3xl bg-villa-turquoise p-6 flex flex-col justify-between" style={{ height: 210 }}>
                  <div className="text-white/60 text-xs tracking-[0.18em] uppercase font-medium">Piscine</div>
                  <div>
                    <div className="text-white font-playfair text-3xl font-bold mb-1">Chauffée</div>
                    <div className="text-white/60 text-sm">Toute la saison</div>
                  </div>
                </div>
              </>
            ) : (
              /* Placeholders while loading */
              <>
                <div className="col-span-1 rounded-3xl bg-villa-offwhite animate-pulse" style={{ gridRow: "span 2", height: 440 }} />
                <div className="rounded-3xl bg-villa-offwhite animate-pulse" style={{ height: 210 }} />
                <div className="rounded-3xl bg-villa-turquoise p-6 flex flex-col justify-between" style={{ height: 210 }}>
                  <div className="text-white/60 text-xs tracking-[0.18em] uppercase font-medium">Piscine</div>
                  <div>
                    <div className="text-white font-playfair text-3xl font-bold mb-1">Chauffée</div>
                    <div className="text-white/60 text-sm">Toute la saison</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
